/**
 * useConnectionVerification — shared channel verification state
 *
 * Wraps GET social/v1/connections/verify, which reports whether each connected
 * channel's credentials are still valid.
 *
 *   - Dashboard  → verify ALL channels          (no query param)
 *   - Channel homepage → verify a SINGLE channel (?channels=<slug>)
 *
 * Response shape (see ConnectionVerifyResult in shared/types):
 *   {
 *     data: {
 *       googlercs: [],
 *       sms:      [{ id, app_id, is_verified: false, error: {...} }],
 *       whatsapp: [{ id, app_id, is_verified: true }]
 *     },
 *     message, success
 *   }
 *
 * apiInterceptor unwraps the envelope and returns the inner `data` object.
 *
 * Verification entries live in module-level reactive state keyed by channel, so
 * every badge on every page reads the same status. A TanStack Query instance is
 * still created per consumer, but only as the mount / window-focus trigger that
 * re-hits the API — the query result is written into the shared state and the
 * getters always read from there.
 *
 * Connection mutations keep that state honest through two imperative helpers
 * that can be called from anywhere (including Options API handlers):
 *
 *   - refreshChannel(channel)    → re-verify one channel after connect / edit
 *   - invalidateChannel(channel) → drop a channel's status after disconnect
 *
 * NOTE: This data is intentionally NOT cached. Verification status reflects the
 * live validity of channel credentials, so the query is configured to always
 * refetch and is excluded from the persisted query cache (see queryClient.js).
 */
import { computed, reactive, unref, type MaybeRef } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import type { ConnectionVerifyResult, ConnectionVerifyEntry, VerifiableChannel } from '@/shared/types'

const EMPTY_RESULT: ConnectionVerifyResult = {}

/** Every channel the verify API can report on. */
const ALL_VERIFIABLE_CHANNELS: VerifiableChannel[] = ['whatsapp', 'googlercs', 'sms', 'email']

/**
 * Shared verification entries, channel -> entries. An empty list means
 * "unknown / not connected", which `isChannelVerified` surfaces as `null`.
 */
const verificationEntries = reactive<Record<VerifiableChannel, ConnectionVerifyEntry[]>>({
  whatsapp: [],
  googlercs: [],
  sms: [],
  email: [],
})

/**
 * Bumped whenever a channel is invalidated locally (disconnect). A verify
 * response that was already in flight when that happened is discarded instead
 * of resurrecting the stale status.
 */
const channelGeneration: Record<VerifiableChannel, number> = {
  whatsapp: 0,
  googlercs: 0,
  sms: 0,
  email: 0,
}

/** In-flight requests keyed by the sorted channel list, to avoid duplicates. */
const inFlightRequests = new Map<string, Promise<ConnectionVerifyResult>>()

function requestKey(channels: VerifiableChannel[]): string {
  return [...channels].sort().join(',')
}

/**
 * Fetches verification status.
 *
 * @param channels - Channel slugs to verify. Omit / empty for all channels.
 */
async function fetchVerification(channels: VerifiableChannel[]): Promise<ConnectionVerifyResult> {
  let url = environment('VUE_APP_API_URL') + 'social/v1/connections/verify'
  if (channels.length) {
    url += `?channels=${encodeURIComponent(channels.join(','))}`
  }
  const r = (await apiInterceptor.get(url)) as ConnectionVerifyResult | null
  return r ?? EMPTY_RESULT
}

/**
 * Writes a verify response into the shared state.
 *
 * Only the channels that were asked for are written, so a single-channel
 * refresh never wipes the other channels' status. Channels invalidated while
 * the request was in flight are skipped.
 */
function applyResult(
  channels: VerifiableChannel[],
  generations: Record<string, number>,
  result: ConnectionVerifyResult
): void {
  const target = channels.length ? channels : ALL_VERIFIABLE_CHANNELS
  target.forEach((channel) => {
    if (channelGeneration[channel] !== generations[channel]) return
    verificationEntries[channel] = result?.[channel] ?? []
  })
}

/**
 * Fetches and stores verification status, reusing an identical in-flight
 * request instead of firing a second one. Rejections are propagated so callers
 * can surface the error; the shared state is left untouched on failure, which
 * keeps a channel from flipping to a misleading status.
 */
function requestVerification(channels: VerifiableChannel[]): Promise<ConnectionVerifyResult> {
  const key = requestKey(channels)
  const existing = inFlightRequests.get(key)
  if (existing) return existing

  const generations: Record<string, number> = {}
  const target = channels.length ? channels : ALL_VERIFIABLE_CHANNELS
  target.forEach((channel) => {
    generations[channel] = channelGeneration[channel]
  })

  const request = fetchVerification(channels)
    .then((result) => {
      applyResult(channels, generations, result)
      return result
    })
    .finally(() => {
      inFlightRequests.delete(key)
    })

  inFlightRequests.set(key, request)
  return request
}

/**
 * Re-verifies a single channel. Call this after a connection is created or any
 * of its settings are saved, so the badge reflects the new credentials.
 *
 * Failures are swallowed: the previously known status stays on screen rather
 * than the channel being shown as verified (or failed) on a guess.
 */
export async function refreshChannel(channel: VerifiableChannel): Promise<void> {
  try {
    await requestVerification([channel])
  } catch (error) {
    console.error('Failed to refresh verification status for ' + channel, error)
  }
}

/**
 * Clears a channel's verification status locally, without waiting on the API.
 * Call this after a disconnect: the channel is no longer verified and its
 * stored error must not linger.
 */
export function invalidateChannel(channel: VerifiableChannel): void {
  channelGeneration[channel] += 1
  verificationEntries[channel] = []
  // Any pending request could still carry this channel, so stop reusing them.
  inFlightRequests.clear()
}

/** All verification entries for a channel. Empty array when unknown. */
export function entriesFor(channel: VerifiableChannel): ConnectionVerifyEntry[] {
  return verificationEntries[channel] ?? []
}

/**
 * Verification status for a channel.
 *
 * A channel is considered verified only when it has at least one entry and
 * every entry reports `is_verified === true`. Returns `null` when there are
 * no entries yet (status unknown / not loaded / channel not connected), so
 * callers can choose to hide the badge instead of showing a false "failed".
 */
export function isChannelVerified(channel: VerifiableChannel): boolean | null {
  const entries = entriesFor(channel)
  if (!entries.length) return null
  return entries.every((e) => e.is_verified === true)
}

/** First verification error message for a channel, if any. */
export function verificationErrorFor(channel: VerifiableChannel): string | null {
  const failing = entriesFor(channel).find((e) => !e.is_verified && e.error)
  if (!failing?.error) return null
  return failing.error.detail || failing.error.title || 'Verification failed'
}

/**
 * Reactive verification state.
 *
 * @param channelsRef - Optional reactive list of channels to scope the request.
 *                      Pass nothing to verify every channel (dashboard).
 * @param enabledRef  - Optional flag to defer the request until ready.
 */
export function useConnectionVerification(
  channelsRef?: MaybeRef<VerifiableChannel[] | undefined>,
  enabledRef?: MaybeRef<boolean>
) {
  const channels = computed<VerifiableChannel[]>(() => unref(channelsRef) ?? [])

  const { isLoading, isFetching, isError, error, refetch } = useQuery({
    // Keying by the sorted channel list keeps the all-channels result separate
    // from any per-channel result.
    queryKey: computed(() => ['connection-verify', requestKey(channels.value)]),
    queryFn: () => requestVerification(channels.value),
    enabled: computed(() => (enabledRef === undefined ? true : Boolean(unref(enabledRef)))),
    // Verification status must always be live — never serve a cached result.
    // staleTime 0 marks every result immediately stale, gcTime 0 drops it from
    // cache as soon as no component uses it, and the refetch flags re-hit the
    // API on every mount / remount and window refocus.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })

  const result = computed<ConnectionVerifyResult>(() => ({ ...verificationEntries }))

  return {
    result,
    entriesFor,
    isChannelVerified,
    verificationErrorFor,
    refreshChannel,
    invalidateChannel,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  }
}
